import Statistics from './Pages/Dashboard/Statistics';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './Pages/Login/Login';
import Events from './Pages/Dashboard/Events';
import LegalDocuments from './Pages/Dashboard/LegalDocuments';
import Defaults from './Pages/Dashboard/Defaults';
import Campaigns from './Pages/Dashboard/Campaigns';
import Users from './Pages/Dashboard/Users';
import Loader from './Components/Loader/Loader';
import Sidebar from './Components/Sidebar/Sidebar';
import { supabase } from './Services/supabaseClient';
import { getMyAccount } from './Services/arndAPI';
import Photographers from './Pages/Dashboard/Photographers';
import Photos from './Pages/Dashboard/Photos';
import Survey from './Pages/Dashboard/Survey';
import Event from './Pages/Dashboard/Event';
import EventAttendees from './Pages/Dashboard/EventAttendees';
import EventOps from './Pages/Dashboard/EventOps';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // SCRUM-271: access is the API's say, not a hardcoded email. Whoever the
  // Supabase session belongs to, the dashboard opens only if the API-side
  // account carries role ADMIN; anyone else is signed out and told why.
  const [denied, setDenied] = useState(null);
  // SCRUM-452: a non-admin assigned to events runs their Event Ops only.
  // The API enforces it; this just keeps them off pages that would 403.
  const [operator, setOperator] = useState(false);

  const resolveAccess = async (sessionUser) => {
    try {
      const account = await getMyAccount();
      const isOperator = account?.role !== 'ADMIN' && account?.operatorEventIds?.length > 0;
      if (account?.role === 'ADMIN' || isOperator) {
        setDenied(null);
        setOperator(isOperator);
        setUser(sessionUser);
        return true;
      }
      setDenied(sessionUser?.email || 'this account');
    } catch (err) {
      setDenied(`${sessionUser?.email || 'this account'} (${err.message})`);
    }
    localStorage.removeItem('a_access_token');
    try { await supabase.auth.signOut(); } catch { /* already out */ }
    setUser(null);
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let sessionUser = null;
      // Prefer the supabase client's own persisted, auto-refreshing session.
      try {
        const { data } = await supabase.auth.getSession();
        sessionUser = data?.session?.user ?? null;
      } catch { /* fall through to snapshot */ }
      // Fallback: legacy token snapshot (also used by tooling logins).
      if (!sessionUser) {
        const token = localStorage.getItem('a_access_token');
        if (token) {
          const { data } = await supabase.auth.getUser(token);
          sessionUser = data?.user ?? null;
          if (!sessionUser) localStorage.removeItem('a_access_token');
        }
      }
      if (sessionUser && !cancelled) await resolveAccess(sessionUser);
      // Only now decide: keeps deep links from bouncing to /login while the
      // async check is still in flight.
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <Router>
      {!user ? (
        <Routes>
          <Route
            path="/login"
            element={<Login onLogin={resolveAccess} denied={denied} />}
          />
          <Route
            path="*"
            element={<Navigate to="/login" replace />}
          />
        </Routes>
      ) : (
        <div style={{ display: 'flex' }}>
          <Sidebar operator={operator} />
          {/* SCRUM-450: no padding/centering here — every page's own <Page>
              frame owns that now, so it's the same on every route. */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {operator ? (
            <Routes>
              <Route path="/events" element={<Events user={user} operator />} />
              <Route path="/events/ops/:id" element={<EventOps user={user} />} />
              <Route path="*" element={<Navigate to="/events" replace />} />
            </Routes>
            ) : (
            <Routes>
              <Route path="/events" element={<Events user={user} />} />
              <Route path="/events/:id" element={<Event user={user} />} />
              <Route path="/events/attendees/:id" element={<EventAttendees user={user} />} />
              <Route path="/events/ops/:id" element={<EventOps user={user} />} />
              <Route path="/legal-documents" element={<LegalDocuments user={user} />} />
              <Route path="/defaults" element={<Defaults user={user} />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/prices" element={<Navigate to="/defaults" replace />} />
              <Route path="/users" element={<Users user={user} />} />
              <Route path="/photographers" element={<Photographers user={user}/>} />
              <Route path="/photos" element={<Photos user={user}/>} />
              <Route path="/survey" element={<Survey user={user}/>} />
              <Route path="/dashboard" element={<Statistics user={user}/>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            )}
          </div>
        </div>
      )}
    </Router>
  );
}

export default App;
